// src/components/Table.tsx
import React, { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import Button from "./Button";
export type Column<T> = {
  header: string;
  accessor?: keyof T | string;
  sortable?: boolean;
  cell?: (row: T) => React.ReactNode;
  width?: string;
};

type Props<T> = {
  title?: string;
  data: T[];
  columns: Column<T>[];
  onAdd?: (values?: any) => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  showActions?: boolean;
  className?: string;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  loading?: boolean;
  onRowClick?: (row: T) => void | Promise<void>;
  mobileHeaderFields?: (keyof T | string)[];
  renderActions?: (row: T) => React.ReactNode;
  serverSide?: boolean;
  total?: number; // total rows available on server
  currentPage?: number; // controlled page number when serverSide true
  pageSize?: number; // controlled pageSize when serverSide true
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  from?: number;
  to?: number;
};

function compareValues(a: any, b: any, dir: "asc" | "desc") {
  if (a == null) return dir === "asc" ? -1 : 1;
  if (b == null) return dir === "asc" ? 1 : -1;
  if (typeof a === "number" && typeof b === "number")
    return dir === "asc" ? a - b : b - a;
  const sa = String(a).toLowerCase();
  const sb = String(b).toLowerCase();
  if (sa < sb) return dir === "asc" ? -1 : 1;
  if (sa > sb) return dir === "asc" ? 1 : -1;
  return 0;
}

export default function Table<T extends Record<string, any>>({
  title,
  data,
  columns,
  onAdd,
  onEdit,
  onDelete,
  showActions = false,
  className = "",
  defaultPageSize = 20,
  pageSizeOptions = [5, 10, 20],
  loading = false,
  onRowClick,
  mobileHeaderFields,
  renderActions,
  // server-side props
  serverSide = false,
  total: serverTotal = 0,
  currentPage: serverCurrentPage,
  pageSize: serverPageSize,
  onPageChange,
  onPageSizeChange,
  from,
  to,
}: Props<T>) {
  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  // local pagination state (used when NOT serverSide)
  const [localPageSize, setLocalPageSize] = useState<number>(defaultPageSize);
  const [localPage, setLocalPage] = useState<number>(1);

  // choose page & pageSize depending on serverSide flag
  const pageSize = serverSide
    ? (serverPageSize ?? defaultPageSize)
    : localPageSize;
  const page = serverSide ? (serverCurrentPage ?? 1) : localPage;

  // Filter + sort (client-side only when not serverSide)
  const filtered = useMemo(() => {
    if (serverSide) {
      // server handles filtering/sorting; we just show whatever data was returned.
      return [...data];
    }

    let rows = [...data];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((r) =>
        Object.values(r).some((v) =>
          String(v ?? "")
            .toLowerCase()
            .includes(q),
        ),
      );
    }
    if (sortColumn) {
      rows.sort((a, b) =>
        compareValues(
          String(sortColumn).includes(".")
            ? getNested(a, sortColumn)
            : a[sortColumn],
          String(sortColumn).includes(".")
            ? getNested(b, sortColumn)
            : b[sortColumn],
          sortDir,
        ),
      );
    }
    return rows;
  }, [data, search, sortColumn, sortDir, serverSide]);

  // Pagination derived
  const total = serverSide ? (serverTotal ?? 0) : filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const paged = useMemo(() => {
    if (serverSide) {
      return data; // server already returned the correct page
    }
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, data, page, pageSize, serverSide]);

  // Reset local page when search/pageSize changes
  React.useEffect(() => {
    if (!serverSide) setLocalPage(1);
  }, [search, localPageSize, serverSide]);

  function toggleSort(accessor?: keyof T | string, sortable?: boolean) {
    if (!sortable || !accessor) return;
    const acc = String(accessor);
    if (sortColumn !== acc) {
      setSortColumn(acc);
      setSortDir("asc");
    } else {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    }
  }

  function getNested(obj: any, path: string) {
    return path.split(".").reduce((o, k) => (o ? o[k] : undefined), obj);
  }

  // helpers to change page / pageSize (work for both modes)
  const goToPage = (p: number) => {
    if (serverSide) {
      onPageChange?.(p);
    } else {
      setLocalPage(p);
    }
  };

  const changePageSize = (s: number) => {
    if (serverSide) {
      onPageSizeChange?.(s);
      onPageChange?.(1); // parent should reset to first page
    } else {
      setLocalPageSize(s);
      setLocalPage(1);
    }
  };

  return (
    <div
      className={`light-bg-color rounded-3xl shadow-xl p-5 overflow-hidden ${className}`}
    >
      {/* Replace the spinner loader with a skeleton loader */}
      {loading && (
        <div className="space-y-4 animate-pulse py-4">
          {/* top summary skeleton */}
          <div className="flex items-center justify-between gap-4">
            <div className="h-6 bg-slate-200 rounded w-1/4 sm:h-3" />
            <div className="h-8 bg-slate-200 rounded w-24 " />
          </div>

          {/* Desktop table skeleton */}
          <div className="hidden md:block">
            <div className="rounded-2xl overflow-auto border border-slate-100 shadow-sm">
              <table className="min-w-full bg-white">
                <thead className="bg-slate-50">
                  <tr>
                    {columns.map((_, i) => (
                      <th key={i} className="px-6 py-4">
                        <div className="h-4 main-bg-color rounded w-24" />
                      </th>
                    ))}
                    {showActions && (
                      <th className="px-6 py-4">
                        <div className="h-4 main-bg-color rounded w-16" />
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody className="bg-white">
                  {Array.from({ length: Math.min(5, pageSize ?? 5) }).map(
                    (_, rIdx) => (
                      <tr key={rIdx} className="border-b border-gray-100">
                        {columns.map((_, cIdx) => (
                          <td key={cIdx} className="px-6 py-4">
                            <div className="h-4 bg-slate-200 rounded w-full" />
                          </td>
                        ))}
                        {showActions && (
                          <td className="px-6 py-4">
                            <div className="h-4 bg-slate-200 rounded w-24" />
                          </td>
                        )}
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile skeleton */}
          <div className="md:hidden space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-slate-200 rounded-full" />
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-slate-200 rounded w-1/2" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {!loading && (
        <>
          {/* Top bar */}
          <div className="flex flex-row items-start md:items-center justify-between  mb-6">
            <div className="flex flex-row md:items-center gap-3 w-auto">
              <div className="text-sm text-color  mr-4">
                {serverSide ? (
                  <>
                    Showing {from ?? 0} – {to ?? 0} of {total}
                  </>
                ) : (
                  <>
                    Showing {total === 0 ? 0 : (page - 1) * pageSize + 1} –{" "}
                    {Math.min(page * pageSize, total)} of {total}
                  </>
                )}
              </div>

              {title && (
                <h3 className="text-2xl font-semibold text-color">{title}</h3>
              )}
            </div>

            <div className="flex items-center w-auto">
              {onAdd && (
                <Button
                  onClick={onAdd}
                  variant="primary"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-white font-semibold shadow hover:shadow-lg transform hover:-translate-y-0.5 transition"
                  icon="mdi:plus"
                >
                  <span className="hidden sm:inline">{title ?? "ADD"}</span>
                </Button>
              )}
            </div>
          </div>

          {/* Desktop table */}
          <div className="hidden md:block">
            <div className="rounded-2xl overflow-auto border border-slate-100 shadow-sm scrollbar-thin-gray">
              <table className="min-w-full bg-white">
                <thead className="main-bg-color">
                  <tr>
                    {columns.map((col: any, i: number) => (
                      <th
                        key={i}
                        className={`px-6 py-4 text-sm text-color font-medium uppercase tracking-wider select-none border-r  ${col.width ?? ""}`}
                        onClick={() => toggleSort(col.accessor, col.sortable)}
                      >
                        <div className="flex items-center gap-2">
                          <span>{col.header}</span>
                          {/* {col.sortable && (
                            <span className="text-xs text-slate-400">
                              {sortColumn === String(col.accessor) ? (sortDir === "asc" ? <Icon icon="mdi:arrow-up" width={12} /> : <Icon icon="mdi:arrow-down" width={12} />) : <Icon icon="mdi:swap-vertical" width={12} />}
                            </span>
                          )} */}
                        </div>
                      </th>
                    ))}
                    {showActions && (
                      <th className="px-6 py-4 w-36 text-sm text-color uppercase">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>

                <tbody className="bg-white">
                  {paged.length === 0 && (
                    <tr>
                      <td
                        colSpan={columns.length + (showActions ? 1 : 0)}
                        className="px-6 py-12 text-center text-slate-400"
                      >
                        No records found
                      </td>
                    </tr>
                  )}

                  {paged.map((row: any, idx: number) => {
                    const key = (row &&
                      (row.id ?? row.key ?? idx)) as React.Key;
                    return (
                      <tr
                        key={key}
                        className={`border-b border-gray-200 last:border-b-0 hover:bg-slate-50 transition ${onRowClick ? "cursor-pointer" : ""}`}
                      >
                        {columns.map((col: any, cIdx: number) => (
                          <td
                            key={cIdx}
                            className="px-6 py-4 align-top text-sm text-slate-700"
                            onClick={() => onRowClick?.(row)}
                          >
                            {col.cell
                              ? col.cell(row)
                              : col.accessor
                                ? String((row as any)[col.accessor])
                                : null}
                          </td>
                        ))}

                        {showActions && (
                          <td className="px-6 py-4 text-sm">
                            {renderActions ? (
                              <div>{renderActions(row)}</div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => onEdit?.(row)}
                                  className="p-2 rounded-md hover:bg-slate-100 transition cursor-pointer"
                                  title="Edit"
                                >
                                  <Icon icon="mdi:pencil" width={18} />
                                </button>
                                <button
                                  onClick={() => onDelete?.(row)}
                                  className="p-2 rounded-md hover:bg-rose-50 transition cursor-pointer"
                                  title="Delete"
                                >
                                  <Icon icon="mdi:delete" width={18} />
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile: accordion list */}
          <div className="md:hidden space-y-3">
            {paged.length === 0 && (
              <div className="px-4 py-8 text-center text-slate-400 border rounded-lg">
                No records found
              </div>
            )}

            {paged.map((row: any, idx: number) => {
              const key = (row && (row.id ?? row.key ?? idx)) as React.Key;

              const colByAccessor: Record<string, any> = {};
              columns.forEach((c: any) => {
                if (c.accessor) colByAccessor[String(c.accessor)] = c;
              });

              const headerFields: string[] =
                mobileHeaderFields && mobileHeaderFields.length > 0
                  ? mobileHeaderFields.map((f: any) => String(f))
                  : [
                      "profile",
                      "name",
                      "email",
                      "code",
                      "transaction_id",
                      "model",
                    ];
              const headerFieldSet = new Set(headerFields);

              const renderProfile = (row: any) => {
                // Use the second header field as the avatar label (e.g., 'model', 'name')
                const labelField = headerFields[1] ?? "name";
                const avatarLabel = String(row[labelField] ?? "-")
                  .charAt(0)
                  .toUpperCase();

                return (
                  <div className="relative">
                    {row.profile && row.profile.trim() !== "" ? (
                      <img
                        src={row.profile}
                        alt={row[labelField]}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center bg-blue-500">
                        <span className="text-white font-semibold">
                          {avatarLabel}
                        </span>
                      </div>
                    )}
                    {(row.raw?.status === "Active" ||
                      row.raw?.status === "Inactive") && (
                      <span
                        className={`absolute top-0 right-0 w-3 h-3 border-2 border-white rounded-full ${row.raw.status === "Active" ? "bg-green-500" : "bg-red-500"}`}
                      />
                    )}
                  </div>
                );
              };

              return (
                <details
                  key={key}
                  className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
                >
                  <summary className="flex items-center justify-between px-4 py-3 cursor-pointer select-none">
                    <div
                      className="flex items-center gap-3"
                      onClick={() => onRowClick?.(row)}
                    >
                      {headerFields[0] === "profile"
                        ? renderProfile(row)
                        : columns.length > 0 && columns[0].cell
                          ? columns[0].cell(row)
                          : null}
                      <div className="flex flex-col">
                        {headerFields.slice(1).map((field) => (
                          <div
                            key={field}
                            className={`text-sm font-semibold text-slate-800 ${field === headerFields[1] ? "" : "text-xs text-slate-400"}`}
                          >
                            {row[field] ?? ""}
                          </div>
                        ))}
                      </div>
                    </div>
                    <Icon
                      icon="mdi:chevron-down"
                      width={18}
                      className="text-slate-400"
                    />
                  </summary>

                  <div className="px-4 pb-4 pt-2 border-t border-slate-100">
                    {columns
                      .filter(
                        (col: any) => !headerFieldSet.has(String(col.accessor)),
                      )
                      .map((col: any, cIdx: number) => (
                        <div
                          key={cIdx}
                          className="flex justify-between py-2 text-sm"
                        >
                          <div className="text-slate-500 flex">
                            {col.header}
                            <span>:</span>{" "}
                          </div>

                          <div className="text-slate-800 ps-2 font-medium">
                            {col.cell
                              ? col.cell(row)
                              : col.accessor
                                ? String(row[col.accessor] ?? "-")
                                : "-"}
                          </div>
                        </div>
                      ))}

                    {showActions && (
                      <div className="mt-3">
                        {renderActions ? (
                          <div>{renderActions(row)}</div>
                        ) : (
                          <div className="flex gap-3">
                            <button
                              onClick={() => onEdit?.(row)}
                              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-200"
                            >
                              <Icon icon="mdi:pencil" width={16} /> Edit
                            </button>
                            <button
                              onClick={() => onDelete?.(row)}
                              className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-rose-200 text-rose-600"
                            >
                              <Icon icon="mdi:delete" width={16} /> Delete
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </details>
              );
            })}
          </div>

          {/* Pagination footer */}
          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-slate-600">
            <div className="flex items-center gap-3 text-color ">
              <span>Rows per page</span>
              <select
                className="border rounded-lg p-1 text-sm shadow-sm text-color "
                value={pageSize}
                onChange={(e) => changePageSize(Number(e.target.value))}
              >
                {pageSizeOptions.map((s: number) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 ">
              <button
                onClick={() => goToPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="px-3 py-1 rounded-lg border disabled:opacity-50 text-color "
              >
                Prev
              </button>

              <div className="px-4 py-1 border rounded-lg bg-slate-50">
                {page} / {totalPages}
              </div>

              <button
                onClick={() => goToPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded-lg border disabled:opacity-50 text-color "
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
